import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Star, Trash2, Edit, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";

interface Answer {
  id: string;
  question_text: string;
  answer_text: string;
  overall_score: number;
  is_favorite: boolean;
  tags: string[];
  notes: string;
  created_at: string;
}

const AnswerLibrary = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    loadAnswers();
  }, []);

  const loadAnswers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data, error } = await supabase
        .from('answer_library')
        .select(`
          *,
          interview_answers (
            answer_text,
            overall_score
          ),
          interview_questions (
            text
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedAnswers = data.map((item: any) => ({
        id: item.id,
        question_text: item.interview_questions?.text || 'Unknown Question',
        answer_text: item.interview_answers?.answer_text || '',
        overall_score: item.interview_answers?.overall_score || 0,
        is_favorite: item.is_favorite,
        tags: item.tags || [],
        notes: item.notes || '',
        created_at: item.created_at
      }));

      setAnswers(formattedAnswers);
    } catch (error) {
      console.error('Error loading answers:', error);
      toast({
        title: "Error",
        description: "Failed to load answer library",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (answerId: string, currentFavorite: boolean) => {
    try {
      const { error } = await supabase
        .from('answer_library')
        .update({ is_favorite: !currentFavorite })
        .eq('id', answerId);

      if (error) throw error;

      setAnswers(answers.map(a => 
        a.id === answerId ? { ...a, is_favorite: !currentFavorite } : a
      ));

      toast({
        title: currentFavorite ? "Removed from favorites" : "Added to favorites"
      });
    } catch (error) {
      console.error('Error updating favorite:', error);
      toast({
        title: "Error",
        description: "Failed to update favorite status",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (answerId: string) => {
    if (!confirm("Are you sure you want to delete this answer?")) return;

    try {
      const { error } = await supabase
        .from('answer_library')
        .delete()
        .eq('id', answerId);

      if (error) throw error;

      setAnswers(answers.filter(a => a.id !== answerId));
      toast({
        title: "Answer deleted"
      });
    } catch (error) {
      console.error('Error deleting answer:', error);
      toast({
        title: "Error",
        description: "Failed to delete answer",
        variant: "destructive"
      });
    }
  };

  const getFilteredAnswers = () => {
    switch (activeTab) {
      case 'favorites':
        return answers.filter(a => a.is_favorite);
      case 'high-scores':
        return answers.filter(a => a.overall_score >= 9);
      case 'need-practice':
        return answers.filter(a => a.overall_score < 7);
      default:
        return answers;
    }
  };

  const filteredAnswers = getFilteredAnswers();

  const getScoreColor = (score: number) => {
    if (score >= 8) return "text-green-500";
    if (score >= 6) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <>
      <Helmet>
        <title>Answer Library | AI Interview Prep</title>
      </Helmet>

      
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
          <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold">My Answer Library</h1>
                <p className="text-muted-foreground mt-2">
                  Your collection of practiced interview answers
                </p>
              </div>
              <Button onClick={() => navigate('/app/ai-interview-prep')}>
                Back to AI Interview Prep
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All Answers ({answers.length})</TabsTrigger>
                <TabsTrigger value="favorites">
                  Favorites ({answers.filter(a => a.is_favorite).length})
                </TabsTrigger>
                <TabsTrigger value="high-scores">
                  High Scores ({answers.filter(a => a.overall_score >= 9).length})
                </TabsTrigger>
                <TabsTrigger value="need-practice">
                  Need Practice ({answers.filter(a => a.overall_score < 7).length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="space-y-4 mt-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p>Loading answers...</p>
                  </div>
                ) : filteredAnswers.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-muted-foreground mb-4">
                        No answers found in this category
                      </p>
                      <Button onClick={() => navigate('/app/ai-interview-prep/practice/quick')}>
                        Start Practicing
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredAnswers.map((answer) => (
                      <Card key={answer.id} className="hover:border-primary/50 transition-all">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-lg">{answer.question_text}</CardTitle>
                              <CardDescription className="mt-2">
                                Practiced on {new Date(answer.created_at).toLocaleDateString()}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={getScoreColor(answer.overall_score)}>
                                {answer.overall_score.toFixed(1)}/10
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleFavorite(answer.id, answer.is_favorite)}
                              >
                                <Star
                                  className={`w-4 h-4 ${answer.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`}
                                />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="bg-muted/30 p-4 rounded text-sm">
                            {answer.answer_text.substring(0, 200)}
                            {answer.answer_text.length > 200 && '...'}
                          </div>
                          {answer.tags.length > 0 && (
                            <div className="flex gap-2">
                              {answer.tags.map((tag, i) => (
                                <Badge key={i} variant="outline">{tag}</Badge>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm">
                              <Play className="w-4 h-4 mr-2" />
                              Practice Again
                            </Button>
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Notes
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(answer.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      
    </>
  );
};

export default AnswerLibrary;
